import { Checkbox, message } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { UploadFile } from 'antd/lib/upload/interface';
import { Button, Col, FormItem, Input, Select } from 'components';
import { theme } from 'configs/styled';
import Divider from 'containers/DriverPortalPage/components/Divider';
import DriverPortalLayout from 'containers/DriverPortalPage/components/DriverPortalLayout';
import TextBold from 'containers/DriverPortalPage/components/TextBold';
import TextRegular from 'containers/DriverPortalPage/components/TextRegular';
import { FormikProps, FormikValues, getIn, useFormik } from 'formik';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addressService,
  authenticationService,
  documentReaderService,
  mainService,
} from 'services/driverPortal';
import { ReaderType } from 'services/driverPortal/documentReader/DocumentReaderService';
import * as Yup from 'yup';
import Loading from '../../../../components/ReturnOrderModal/Loading';
import ConfirmReturnModal from '../components/ConfirmReturnModal';
import HeaderPortal from '../components/HeaderPortal';
import Information from '../components/Information';
import ReturnButton from '../components/ReturnButton';
import UploadImageForm from '../components/UploadImageForm';
import HouseRegistrationExampleImage from '../../../../assets/images/driver-portal/preview/House-Registration-Example.png';
import { isPdfFile } from '../../../../utils/isFilePdf';

interface IAddressOption {
  _id: string;
  title: string;
  value: string;
}

interface IHouseRegistrationRequest {
  document: { image?: string };
  address: {
    useIdCardAddress: boolean;
    address?: string;
    province?: string;
    district?: string;
    subDistrict?: string;
    postCode?: string;
  };
  fileData: any;
  error: { docError?: string };
}

interface IFilesUpload {
  document?: FormData;
}

const folderType = 'house-registration';

const THAI_MONTHS: Record<string, string> = {
  'ม.ค.': '01',
  'ก.พ.': '02',
  'มี.ค.': '03',
  'เม.ย.': '04',
  'พ.ค.': '05',
  'มิ.ย.': '06',
  'ก.ค.': '07',
  'ส.ค.': '08',
  'ก.ย.': '09',
  'ต.ค.': '10',
  'พ.ย.': '11',
  'ธ.ค.': '12',
};

const parseThaiDateToApiFormat = (birthDate: string) => {
  let isoDate: string | null = null;

  if (/^\d{4}-\d{2}-\d{2}/.test(birthDate)) {
    isoDate = birthDate.substring(0, 10);
  } else {
    const parts = birthDate?.trim().split(' ');
    if (parts?.length === 3) {
      const [day, monthTh, yearBE] = parts;
      const month = THAI_MONTHS[monthTh];
      if (month) {
        const yearCE = parseInt(yearBE, 10) - 543;
        const dd = String(parseInt(day, 10)).padStart(2, '0');
        isoDate = `${yearCE}-${month}-${dd}`;
      }
    }
  }

  return isoDate;
};

const validateSchema = Yup.object()
  .shape({
    document: Yup.object().shape({
      image: Yup.string(),
    }),
    address: Yup.object().shape({
      useIdCardAddress: Yup.boolean(),
    }),
  })
  .test('image-required', '', function (values) {
    const useIdCard = values?.address?.useIdCardAddress;
    const image = values?.document?.image;
    if (!useIdCard && !image) {
      return this.createError({
        path: 'document.image',
        message: 'กรุณาอัพโหลดสำเนาทะเบียนบ้าน',
      });
    }
    return true;
  });

const HouseRegistrationPage: React.FC = () => {
  const { search, pushUrl } = useQueryParams();
  const [isShowModal, setIsShowModal] = useState(false);
  const [, setShowPrompt] = usePrompt();
  const [formDirty, setFormDirty] = useState(false);
  const [imageDeletionFiles, setImageDeletionFiles] = useState<UploadFile[]>(
    [],
  );

  const [houseRegisFileName, setHouseRegisFileName] = useState('');
  const [existingBirthDate, setExistingBirthDate] = useState<any>(null);
  const [filesToUpload, setFilesToUpload] = useState<IFilesUpload>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);

  const [provinceOptions, setProvinceOptions] = useState<IAddressOption[]>([]);
  const [districtOptions, setDistrictOptions] = useState<IAddressOption[]>([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState<
    IAddressOption[]
  >([]);
  const [postalCodeOptions, setPostalCodeOptions] = useState<IAddressOption[]>(
    [],
  );

  const goToMainPage = () => pushUrl('/', search);

  useEffect(() => {
    const { accessToken } = authenticationService.getUserToken() || {};
    if (!accessToken) pushUrl('/login', search);
  }, [pushUrl, search]);

  const loadDistricts = async (provinceId: string) => {
    try {
      const res = await addressService.districts({ provinceId });
      const options: IAddressOption[] = (res.dataList || []).map((d: any) => ({
        _id: d.id.toString(),
        title: d.district_name_th,
        value: d.district_name_th,
      }));
      setDistrictOptions(options);
      return options;
    } catch {
      return [] as IAddressOption[];
    }
  };

  const loadSubDistricts = async (districtId: string) => {
    try {
      const res = await addressService.subDistricts({ districtId });
      const options: IAddressOption[] = (res.dataList || []).map((s: any) => ({
        _id: s.id.toString(),
        title: s.sub_district_name_th,
        value: s.sub_district_name_th,
      }));
      setSubDistrictOptions(options);
      return options;
    } catch {
      return [] as IAddressOption[];
    }
  };

  const loadPostalCodes = async (subDistrictId: string) => {
    try {
      const res = await addressService.postalCodes({ subDistrictId });
      const options: IAddressOption[] = (res.dataList || []).map((p: any) => ({
        _id: p.id.toString(),
        title: p.zipcode ?? '',
        value: p.zipcode ?? '',
      }));
      setPostalCodeOptions(options);
      return options;
    } catch {
      return [] as IAddressOption[];
    }
  };

  const formik = useFormik<IHouseRegistrationRequest>({
    initialValues: {
      document: { image: undefined },
      address: {
        useIdCardAddress: false,
        address: '',
        province: undefined,
        district: undefined,
        subDistrict: undefined,
        postCode: undefined,
      },
      fileData: null,
      error: { docError: undefined },
    },
    validationSchema: validateSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async () => {
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        const addr = formik.values.address;
        const fd = new FormData();
        fd.append('tab', 'house_registration');
        fd.append('useIdCardAddress', addr.useIdCardAddress ? '1' : '0');

        if (!addr.useIdCardAddress) {
          fd.append('address', addr.address || '');
          fd.append(
            'province',
            provinceOptions.find(p => p.title === addr.province)?._id || '',
          );
          fd.append(
            'district',
            districtOptions.find(d => d.title === addr.district)?._id || '',
          );
          fd.append(
            'subDistrict',
            subDistrictOptions.find(s => s.title === addr.subDistrict)?._id ||
              '',
          );
          fd.append('postCode', addr.postCode || '');
        }

        if (filesToUpload.document?.get('files')) {
          fd.append(
            'houseRegistrationImage',
            filesToUpload.document.get('files') ?? '',
          );
        } else if (houseRegisFileName && !formik.values.document.image) {
          fd.append('houseRegistrationImage', '');
        }

        const ocrData = formik.values.fileData;
        if (!addr.useIdCardAddress) {
          let birthDateValue: string | null = null;
          if (ocrData?.birth_date) {
            birthDateValue = parseThaiDateToApiFormat(ocrData.birth_date);
            fd.append('birthDate', birthDateValue ?? '');
          }
        }
        if (ocrData?.identification_number) {
          fd.append('idCard', ocrData.identification_number);
        }
        if (ocrData?.first_name) fd.append('firstName', ocrData.first_name);
        if (ocrData?.last_name) fd.append('lastName', ocrData.last_name);

        const res = await addressService.updateInfo(fd);

        const out = (res as any)?.data ?? res;

        if (out?.success === false) {
          const msg =
            out?.errorMessageList?.[0] || out?.message || 'บันทึกไม่สำเร็จ';
          message.error(msg);
          return;
        }

        message.success('บันทึกข้อมูลเรียบร้อยแล้ว');
        goToMainPage();
      } finally {
        setSaveLoading(false);
      }
    },
  });

  const parseThaiAddress = (fullAddress: string) => {
    const subDistrictIdx = fullAddress.search(/ตำบล|แขวง/);
    const address =
      subDistrictIdx > -1
        ? fullAddress.slice(0, subDistrictIdx).trim()
        : fullAddress.trim();

    const subMatch = fullAddress.match(
      /(?:ตำบล|แขวง)(.+?)(?=\s*(?:อำเภอ|เขต)|$)/,
    );
    const subDistrict = subMatch?.[1]?.trim() || '';

    const distMatch = fullAddress.match(
      /(?:อำเภอ|เขต)(.+?)(?=\s*(?:จังหวัด|กรุงเทพมหานคร)|$)/,
    );
    const district = distMatch?.[1]?.trim() || '';

    const provMatch = fullAddress.match(/จังหวัด(.+?)$/);
    const province =
      provMatch?.[1]?.trim() ||
      (fullAddress.includes('กรุงเทพมหานคร') ? 'กรุงเทพมหานคร' : '');

    return { address, subDistrict, district, province };
  };

  const readerHouseRegistration = async (file: File) => {
    try {
      const response = await documentReaderService.readerFile(
        file,
        ReaderType.RIDER_HOUSE_REGISTRATION,
      );

      if (response) {
        const { address, subDistrict, district, province } = parseThaiAddress(
          response.address || '',
        );

        if (response) {
          formik.setFieldValue('address.address', address);
        }

        if (provinceOptions.length > 0) {
          const provinceFind = provinceOptions.find(p => p.title === province);
          response.province = provinceFind?._id;

          if (provinceFind) {
            formik.setFieldValue('address.province', provinceFind.title);

            const districts = await loadDistricts(provinceFind._id);
            const districtFind = districts.find(d => d.title === district);
            response.district = districtFind?._id;

            if (districtFind) {
              formik.setFieldValue('address.district', districtFind.title);

              const subDistricts = await loadSubDistricts(districtFind._id);
              const subDistrictFind = subDistricts.find(
                s => s.title === subDistrict,
              );
              response.subDistrict = subDistrictFind?._id;

              if (subDistrictFind) {
                formik.setFieldValue(
                  'address.subDistrict',
                  subDistrictFind.title,
                );

                const postalCodes = await loadPostalCodes(subDistrictFind._id);
                const postalFind = postalCodes[0];
                if (postalFind) {
                  formik.setFieldValue('address.postCode', postalFind.value);
                  response.postCode = postalFind.value;
                }
              }
            }
          }
        }

        if (response.status) {
          formik.setFieldValue('fileData', response);
          formik.setFieldValue('error', { docError: undefined });
        } else {
          formik.setFieldValue('fileData', {});
          formik.setFieldValue('error', {
            docError:
              response.message?.trim() ||
              response.error_message?.trim() ||
              'ระบบไม่สามารถอ่านข้อมูลจากภาพได้',
          });
        }
      }
    } catch (e) {
      console.log('===== OCR error:', e);
      formik.setFieldValue('fileData', {});
      formik.setFieldValue('error', {
        docError: 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
      });
    }
  };

  // same error-handling pattern as IdentityPage
  const getUploadError = (fd?: {
    status?: boolean;
    message?: string;
    error_message?: string;
  }) => {
    if (!fd) return '';
    if (fd.status === false) {
      setFilesToUpload({ document: undefined });
      formik.setFieldValue('document', { image: undefined });
      return (
        fd.message?.trim() ||
        fd.error_message?.trim() ||
        'ไม่สามารถประมวลผลรูปภาพได้'
      );
    }
    return '';
  };

  useEffect(() => {
    const err = getUploadError(formik.values.fileData);
    if (err) {
      formik.setFieldError('document.image', err);
    } else {
      formik.setFieldError('document.image', undefined as any);
    }
  }, [
    formik.values.fileData?.status,
    formik.values.fileData?.message,
    formik.values.fileData?.error_message,
  ]);

  const handleSelectProvince = async (value: string, option: any) => {
    setDistrictOptions([]);
    setSubDistrictOptions([]);
    setPostalCodeOptions([]);
    formik.setFieldValue('address', {
      ...formik.values.address,
      province: value,
      district: undefined,
      subDistrict: undefined,
      postCode: undefined,
    });
    await loadDistricts(option._id);
  };

  const handleSelectDistrict = async (value: string, option: any) => {
    setSubDistrictOptions([]);
    setPostalCodeOptions([]);
    formik.setFieldValue('address', {
      ...formik.values.address,
      district: value,
      subDistrict: undefined,
      postCode: undefined,
    });
    await loadSubDistricts(option._id);
  };

  const handleSelectSubDistrict = async (value: string, option: any) => {
    setPostalCodeOptions([]);
    formik.setFieldValue('address', {
      ...formik.values.address,
      subDistrict: value,
      postCode: undefined,
    });
    await loadPostalCodes(option._id);
  };

  const handleSelectPostalCode = (value: string) => {
    formik.setFieldValue('address.postCode', value);
  };

  const handleCheckIdCardAddress = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    formik.setFieldValue('address', {
      ...formik.values.address,
      useIdCardAddress: checked,
      ...(checked
        ? {
            address: undefined,
            province: undefined,
            district: undefined,
            subDistrict: undefined,
            postCode: undefined,
          }
        : {}),
    });
  };

  const removeDocument = useCallback(
    (file?: UploadFile) => {
      if (file && !file.fileName?.startsWith('blob:')) {
        setImageDeletionFiles(prev => [...prev, file]);
      }

      const url = formik.values.document?.image;

      if (url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }

      setFilesToUpload({ document: undefined });

      formik.setFieldValue('document', { image: undefined });
      formik.setFieldTouched('document.image', true, false);
      formik.setFieldError('document.image', 'กรุณาอัพโหลดสำเนาทะเบียนบ้าน');

      formik.setFieldValue('fileData', null);
      formik.setFieldValue('error', { docError: undefined });
    },
    [formik],
  );

  useEffect(() => {
    const { dirty, isSubmitting } = formik;
    const isDirty = !isSubmitting && dirty;
    setFormDirty(isDirty);
    setIsShowModal(isDirty);
    setShowPrompt(isDirty);
  }, [formik]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const res = await addressService.provinces();
        const provinceList: IAddressOption[] = (res.dataList || []).map(
          (p: any) => ({
            _id: p.id.toString(),
            title: p.province_name_th,
            value: p.province_name_th,
          }),
        );
        setProvinceOptions(provinceList);

        const response = await mainService.getInfo();
        const data = (response?.data as any)?.potentialLeadHouseRegistration;
        const idCardData = (response?.data as any)?.potentialLeadIdCard;

        const birthDate = data?.birthDate || idCardData?.dateOfBirth;
        if (birthDate) setExistingBirthDate(birthDate);

        if (!data) return;

        setIsApproved(data.statusCode === 'approved');

        if (data.houseRegistrationImage) {
          const image = await mainService.loadImageAxios(
            data.houseRegistrationImage,
          );

          setHouseRegisFileName(data.houseRegistrationImage);
          formik.setFieldValue('fileData', {});
          formik.setFieldValue('document', { image: image?.url });
        }

        const useIdCard = Number(data.useIdCardAddress) === 1;
        if (useIdCard) {
          formik.setFieldValue('address.useIdCardAddress', true);
          return;
        }

        if (data.houseRegistrationAddress)
          formik.setFieldValue(
            'address.address',
            data.houseRegistrationAddress,
          );

        const provinceFound = provinceList.find(
          p => p._id == String(data.province ?? ''),
        );
        if (!provinceFound) return;
        formik.setFieldValue('address.province', provinceFound.title);

        const districts = await loadDistricts(provinceFound._id);
        const districtFound = districts.find(
          d => d._id == String(data.district ?? ''),
        );
        if (!districtFound) return;
        formik.setFieldValue('address.district', districtFound.title);

        const subDistricts = await loadSubDistricts(districtFound._id);
        const subDistrictFound = subDistricts.find(
          s => s._id == String(data.subDistrict ?? ''),
        );
        if (!subDistrictFound) return;
        formik.setFieldValue('address.subDistrict', subDistrictFound.title);

        await loadPostalCodes(subDistrictFound._id);
        formik.setFieldValue('address.postCode', data.postCode);
      } catch (e) {
        console.log('loadPage error:', e);
      } finally {
        setPageLoading(false);
      }
    };
    loadPage();
  }, []);

  const errors = formik.errors || {};
  const shouldDisplayError = (key: string) =>
    getIn(formik.touched, key) && getIn(formik.errors, key);

  const isAddressHidden = formik.values.address.useIdCardAddress;
  const isOcrInProgress =
    !!formik.values.document.image && formik.values.fileData === null;
  const isSubmitDisabled =
    !formik.isValid || formik.isSubmitting || saveLoading || isOcrInProgress;

  return (
    <>
      {pageLoading && <Loading />}
      {formik.values.document.image && !formik.values.fileData && <Loading />}

      <form onSubmit={formik.handleSubmit}>
        <HeaderPortal formik={formik as FormikProps<FormikValues>} />
        <DriverPortalLayout>
          <ReturnButton />

          <Information
            title="ทะเบียนบ้าน"
            description='กรุณากรอกข้อมูล และอัพโหลดเอกสาร แล้วกด "บันทึก"'
          />

          <Divider />

          <FormItem
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              (errors as any).document?.image || formik.values.error?.docError
                ? 'error'
                : ''
            }
            help={
              formik.values.error?.docError ?? (errors as any).document?.image
            }
          >
            <UploadImageForm
              disabled={isApproved}
              title="อัพโหลดทะเบียนบ้าน"
              bulletList={[
                'ไม่แก้ไขและตกแต่งรูป',
                'เห็นข้อมูลชัดเจนและครบถ้วน',
              ]}
              previewTitle="ตัวอย่างทะเบียนบ้าน"
              previewUrl={HouseRegistrationExampleImage}
              onChange={value => {
                const files = value.uploadFileForm
                  ? (value.uploadFileForm.get('files') as Blob)
                  : '';

                setFilesToUpload({
                  ...filesToUpload,
                  document: value.uploadFileForm,
                });

                formik.setFieldValue('document', {
                  image: files ? URL.createObjectURL(files) : '',
                });

                if (value.uploadFileForm?.get('files')) {
                  // @ts-ignore
                  readerHouseRegistration(value.uploadFileForm.get('files'));
                }
              }}
              onRemove={removeDocument}
              nameImage="ทะเบียนบ้าน"
              pathImage={formik.values.document.image}
              folder={folderType}
              isPdf={isPdfFile(houseRegisFileName)}
              fileName={houseRegisFileName}
            />
          </FormItem>

          <Divider />
          <TextBold
            color={theme.palettes.black}
            display="block"
            margin={`0px 0px ${theme.spacing.md}`}
          >
            ที่อยู่ตามทะเบียนบ้าน
          </TextBold>

          <Checkbox
            style={{ marginBottom: theme.spacing.xl }}
            onChange={handleCheckIdCardAddress}
            checked={formik.values.address.useIdCardAddress}
          >
            <TextRegular
              fontSize={theme.fontSize.xs}
              color={theme.palettes.black85}
            >
              ใช้ที่อยู่ตามบัตรประชาชน
            </TextRegular>
          </Checkbox>

          {/* TODO sprint หน้า: uncomment ส่วนที่อยู่ตามทะเบียนบ้าน */}
          {/* {!isAddressHidden && (
            <>
              <FormItem
                label="ที่อยู่"
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayError('address.address') ? 'error' : ''
                }
                help={
                  shouldDisplayError('address.address') &&
                  (errors as any)?.address?.address
                }
              >
                <Input
                  size="large"
                  name="address.address"
                  placeholder="กรอกที่อยู่"
                  value={formik.values.address.address || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="จังหวัด"
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayError('address.province') ? 'error' : ''
                }
                help={
                  shouldDisplayError('address.province') &&
                  (errors as any)?.address?.province
                }
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="เลือกจังหวัด"
                  optionFilterProp="children"
                  options={provinceOptions}
                  value={formik.values.address.province}
                  onChange={handleSelectProvince}
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="เขต/อำเภอ"
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayError('address.district') ? 'error' : ''
                }
                help={
                  shouldDisplayError('address.district') &&
                  (errors as any)?.address?.district
                }
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="เลือกเขต/อำเภอ"
                  optionFilterProp="children"
                  options={districtOptions}
                  value={formik.values.address.district}
                  onChange={handleSelectDistrict}
                  disabled={!formik.values.address.province}
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="แขวง/ตำบล"
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayError('address.subDistrict') ? 'error' : ''
                }
                help={
                  shouldDisplayError('address.subDistrict') &&
                  (errors as any)?.address?.subDistrict
                }
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="เลือกแขวง/ตำบล"
                  optionFilterProp="children"
                  options={subDistrictOptions}
                  value={formik.values.address.subDistrict}
                  onChange={handleSelectSubDistrict}
                  disabled={!formik.values.address.district}
                  onBlur={formik.handleBlur}
                />
              </FormItem>

              <FormItem
                label="รหัสไปรษณีย์"
                labelCol={{ span: 24 }}
                colon={false}
                validateStatus={
                  shouldDisplayError('address.postCode') ? 'error' : ''
                }
                help={
                  shouldDisplayError('address.postCode') &&
                  (errors as any)?.address?.postCode
                }
              >
                <Col span={11}>
                  <Select
                    size="large"
                    showSearch
                    placeholder="เลือกรหัสไปรษณีย์"
                    optionFilterProp="children"
                    options={postalCodeOptions}
                    value={formik.values.address.postCode}
                    onChange={handleSelectPostalCode}
                    disabled={!formik.values.address.subDistrict}
                    onBlur={formik.handleBlur}
                  />
                </Col>
              </FormItem>
            </>
          )} */}

          <Divider />

          <Button
            block
            type={isSubmitDisabled || isApproved ? 'default' : 'primary'}
            size="large"
            disabled={isSubmitDisabled || isApproved}
            htmlType="submit"
            loading={saveLoading}
          >
            บันทึก
          </Button>
        </DriverPortalLayout>
        {formDirty && <ConfirmReturnModal isShow={isShowModal} />}
      </form>
    </>
  );
};

export default HouseRegistrationPage;
